<div class="email-preferences">
  <div class="container">
    <!-- Logo -->
    <div class="logo-wrapper">
      <a routerLink="/">
        <img src="/images/logo.png" alt="JustTeach" class="logo" />
      </a>
    </div>

    <!-- Loading State -->
    @if (pageState() === 'loading') {
      <div class="card">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p>Loading your preferences...</p>
        </div>
      </div>
    }

    <!-- Email Entry (no email provided) -->
    @if (pageState() === 'loaded' && !email()) {
      <div class="card">
        <div class="icon-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
        </div>
        <h1>Email Preferences</h1>
        <p class="subtitle">Enter your email address to manage your subscription preferences.</p>

        <form (ngSubmit)="onEmailSubmit()" class="email-form">
          <div class="input-group">
            <input
              type="email"
              [(ngModel)]="email"
              name="email"
              placeholder="Enter your email address"
              required
            />
            <button type="submit" class="btn-primary">Continue</button>
          </div>
          @if (errorMessage()) {
            <p class="error-text">{{ errorMessage() }}</p>
          }
        </form>
      </div>
    }

    <!-- Preferences Form -->
    @if (pageState() === 'loaded' && email()) {
      <div class="card">
        <div class="icon-wrapper">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
            />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </div>
        <h1>Email Preferences</h1>
        <p class="subtitle">Manage email notifications for <strong>{{ emailMasked() }}</strong></p>

        @if (errorMessage()) {
          <div class="alert alert-error">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{{ errorMessage() }}</span>
          </div>
        }

        <div class="preferences-list">
          <!-- Billing Emails Toggle -->
          <div class="preference-item">
            <div class="preference-info">
              <div class="preference-header">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="preference-icon"
                >
                  <rect width="20" height="14" x="2" y="5" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
                <h3>Billing Emails</h3>
              </div>
              <p>
                Payment confirmations, subscription updates, trial reminders, and billing-related
                notifications.
              </p>
            </div>
            <label class="toggle" [class.disabled]="isUpdating()">
              <input
                type="checkbox"
                [checked]="billingEmailsEnabled()"
                (change)="toggleBillingEmails()"
                [disabled]="isUpdating()"
              />
              <span class="toggle-slider"></span>
            </label>
          </div>

          <!-- Password Reset (always enabled) -->
          <div class="preference-item disabled">
            <div class="preference-info">
              <div class="preference-header">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="preference-icon"
                >
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <h3>Account Security</h3>
              </div>
              <p>Password reset emails and critical security notifications. These cannot be disabled.</p>
            </div>
            <label class="toggle always-on">
              <input type="checkbox" checked disabled />
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>
    }

    <!-- Success State -->
    @if (pageState() === 'success') {
      <div class="card success-card">
        <div class="success-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22,4 12,14.01 9,11.01" />
          </svg>
        </div>
        <h1>Preferences Updated!</h1>
        <p class="subtitle">{{ successMessage() }}</p>
        <button class="btn-secondary" (click)="resetToLoaded()">Back to Preferences</button>
      </div>
    }

    <!-- Error State -->
    @if (pageState() === 'error') {
      <div class="card error-card">
        <div class="error-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1>Something Went Wrong</h1>
        <p class="subtitle">{{ errorMessage() }}</p>
        <button class="btn-primary" (click)="loadPreferences()">Try Again</button>
      </div>
    }

    <!-- Footer -->
    <footer class="footer">
      <p>
        Need help? Contact us at
        <a href="mailto:justteachsupport@justteach.ai">justteachsupport&#64;justteach.ai</a>
      </p>
      <nav class="footer-nav">
        <a routerLink="/privacy-policy">Privacy Policy</a>
        <span class="divider">•</span>
        <a routerLink="/terms-of-use">Terms of Use</a>
      </nav>
    </footer>
  </div>
</div>

